import { useState, useCallback, useRef } from 'react';
import axiosInstance from '../api/axiosInstance';

/**
 * OS Type Enum
 * LINUX = 1, WINDOWS = 2, MAC = 3
 */
const OS_TYPES = {
  LINUX: 1,
  WINDOWS: 2,
  MAC: 3,
};

const OS_TYPE_LABELS = {
  1: 'LINUX',
  2: 'WINDOWS',
  3: 'MAC',
};

/**
 * Custom hook for managing agent installation
 * Handles fetching agents, uploading new agents, deleting agents, and caching
 */
const useAgentManagement = () => {
  const [agents, setAgents] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const cacheRef = useRef({});

  /**
   * Fetch agents for a specific OS
   * Implements caching to avoid redundant API calls
   * @param {number} osId - OS enum value (1=LINUX, 2=WINDOWS, 3=MAC)
   */
  const fetchAgents = useCallback(async (osId = OS_TYPES.LINUX) => {
    // Return cached data if available
    if (cacheRef.current[osId]) {
      setAgents((prev) => ({
        ...prev,
        [osId]: cacheRef.current[osId],
      }));
      return cacheRef.current[osId];
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get('/crawler/agent');

      console.log('API response for agents:', response);
      
      // Filter agents by OS ID and cache the result
      const filteredAgents = response?.filter(
        (agent) => agent.os === osId
      );

      cacheRef.current[osId] = filteredAgents;
      setAgents((prev) => ({
        ...prev,
        [osId]: filteredAgents,
      }));

      return filteredAgents;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch agents';
      setError(errorMessage);
      console.error('Error fetching agents:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Upload a new agent version
   * Sends multipart/form-data with file, os, and version
   * @param {File} file - The .deb file to upload
   * @param {number} osId - OS enum value (1=LINUX, 2=WINDOWS, 3=MAC)
   * @param {string} version - Version string
   */
  const uploadAgent = useCallback(async (file, osId, version) => {
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('os', osId);
      formData.append('version', version);

      const response = await axiosInstance.post('/crawler/upload-agent', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Invalidate cache for this OS to fetch fresh data
      delete cacheRef.current[osId];

      // Re-fetch agents to get the updated list
      await fetchAgents(osId);

      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to upload agent';
      setError(errorMessage);
      console.error('Error uploading agent:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchAgents]);

  /**
   * Clear cache for an OS or all OS
   */
  const clearCache = useCallback((osId = null) => {
    if (osId) {
      delete cacheRef.current[osId];
    } else {
      cacheRef.current = {};
    }
  }, []);

  /**
   * Delete an agent version
   * @param {number} agentId - The agent ID to delete
   * @param {number} osId - OS enum value for cache invalidation
   */
  const deleteAgent = useCallback(async (agentId, osId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.delete(`/crawler/delete_agent_version?agent_id=${agentId}`);

      // Invalidate cache for this OS
      delete cacheRef.current[osId];

      // Re-fetch agents to get the updated list
      await fetchAgents(osId);

      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to delete agent';
      setError(errorMessage);
      console.error('Error deleting agent:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchAgents]);

  

  return {
    agents,
    loading,
    error,
    fetchAgents,
    uploadAgent,
    deleteAgent,
    clearCache,
    OS_TYPES,
  };
};

export default useAgentManagement;
